import { forwardRef, useEffect, useRef } from "react";
import { Link, LinkProps } from "react-router-dom";
import { isLowEndConnection, prefetchRoute } from "@/lib/routePrefetch";

type Props = LinkProps & {
  /** Prefetch as soon as the link enters the viewport. Default true. */
  prefetchOnVisible?: boolean;
};

/**
 * Drop-in replacement for react-router <Link> that opportunistically prefetches
 * the destination route's code chunk on hover, focus, touchstart, or viewport
 * intersection. Mirrors the ergonomics of Next.js automatic link prefetching.
 */
const PrefetchLink = forwardRef<HTMLAnchorElement, Props>(
  ({ to, prefetchOnVisible = true, onMouseEnter, onFocus, onTouchStart, ...rest }, ref) => {
    const localRef = useRef<HTMLAnchorElement | null>(null);
    const path = typeof to === "string" ? to : to.pathname || "";

    const prefetch = () => {
      if (!path || path.startsWith("http") || path.startsWith("#")) return;
      prefetchRoute(path);
    };

    useEffect(() => {
      if (!prefetchOnVisible) return;
      if (isLowEndConnection()) return;
      const el = localRef.current;
      if (!el || typeof IntersectionObserver === "undefined") return;
      const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) {
              prefetch();
              io.disconnect();
              break;
            }
          }
        },
        { rootMargin: "200px" },
      );
      io.observe(el);
      return () => io.disconnect();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [path, prefetchOnVisible]);

    return (
      <Link
        ref={(node) => {
          localRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) (ref as React.MutableRefObject<HTMLAnchorElement | null>).current = node;
        }}
        to={to}
        onMouseEnter={(e) => {
          prefetch();
          onMouseEnter?.(e);
        }}
        onFocus={(e) => {
          prefetch();
          onFocus?.(e);
        }}
        onTouchStart={(e) => {
          prefetch();
          onTouchStart?.(e);
        }}
        {...rest}
      />
    );
  },
);

PrefetchLink.displayName = "PrefetchLink";

export default PrefetchLink;
